import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {hitosApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {FieldGrid, NumberField, TextField, ToggleField} from '../../../../ui/form/fields';
import {
    FormSection,
    buildCardTitle,
    buildSummary,
    hasPersistedId,
    ItemCard,
    EmptyState,
    useProjectReadOnly,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function HitosYObjetivosSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'hitos'],
        proyectoId: props.proyectoId,
        getFn: hitosApi.list,
        createFn: hitosApi.create,
        updateFn: hitosApi.update,
        deleteFn: hitosApi.delete,
    });

    React.useEffect(() => {
        form.reset({...createEmptyProyecto(), hitosYObjetivos: section.data ?? []});
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.hitosYObjetivos ?? []);
    });

    useRegisterSectionSave({
        id: 'hitos',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <HitosSectionFields 
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function HitosSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const hitos = useFieldArray({control: form.control, name: 'hitosYObjetivos'});
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const readOnly = useProjectReadOnly();

    const setAllPublic = (isPublic: boolean) => {
        hitos.fields.forEach((field, index) => {
            form.setValue(`hitosYObjetivos.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentHitos = form.watch('hitosYObjetivos') || [];

    return (
        <FormSection
            id="hitos"
            title={t('hitosYObjetivosSection.title', { count: hitos.fields.length })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('hitosYObjetivosSection.public')}
                        fields={currentHitos}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {hitos.fields.length === 0 && <EmptyState message={t('hitosYObjetivosSection.noMilestones')}/>}
            {hitos.fields.map((field, index) => (
                <ItemCard
                    key={field.id}
                    title={buildCardTitle(t, t('hitosYObjetivosSection.milestone'), index, form.watch(`hitosYObjetivos.${index}.denominacion`), form.watch(`hitosYObjetivos.${index}.localizador`), form.watch(`hitosYObjetivos.${index}.tipo`))}
                    summary={buildSummary(t, form.watch(`hitosYObjetivos.${index}.localizador`), form.watch(`hitosYObjetivos.${index}.denominacion`), form.watch(`hitosYObjetivos.${index}.tipo`))}
                    onRemove={() => hitos.remove(index)}
                    defaultOpen={!hasPersistedId(form.watch(`hitosYObjetivos.${index}.id` as never))}
                    actions={!readOnly &&
                        <ToggleField
                            label={t('hitosYObjetivosSection.public')}
                            name={`hitosYObjetivos.${index}.publico`}
                            register={form.register}
                        />
                    }
                >
                    <FieldGrid>
                        {showId(`hitosYObjetivos.${index}.id`) ?
                            <NumberField label={t('hitosYObjetivosSection.id')} name={`hitosYObjetivos.${index}.id`}
                                         register={form.register}/> : null}
                        <TextField label={t('hitosYObjetivosSection.locator')} name={`hitosYObjetivos.${index}.localizador`}
                                   register={form.register}/>
                        <TextField label={t('hitosYObjetivosSection.denomination')} name={`hitosYObjetivos.${index}.denominacion`}
                                   register={form.register}/>
                        <TextField label={t('hitosYObjetivosSection.type')} name={`hitosYObjetivos.${index}.tipo`} register={form.register}/>
                    </FieldGrid>
                </ItemCard>
            ))}

            {!readOnly ? (
                <button className="btn btnSecondary" type="button"
                        onClick={() => hitos.append({id: 0, localizador: '', denominacion: '', tipo: '', publico: false})}>
                    {t('hitosYObjetivosSection.addMilestone')}
                </button>
            ) : null}
        </FormSection>
    );
}
